<template>
    <Head title="WorkIQ Playground" />
    <!-- Self-contained playground shell: an inline sidebar (back + brand + tab nav) plus a
         content area. Everything lives in this component so the whole staff playground can be
         deleted as one unit when Work IQ graduates out of preview. -->
    <div class="flex min-h-screen bg-valence-grey-50">
        <aside class="w-60 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col px-3 py-4">
            <button
                type="button"
                aria-label="Go back"
                class="self-start flex items-center gap-1 rounded-full bg-white hover:bg-gray-50 px-3 py-1.5 shadow-sm border border-gray-200 text-valence-grey-750"
                data-testid="workiq-playground-back-button"
                @click="handleGoBack">
                <i class="bi bi-chevron-left icon-bold" aria-hidden="true"></i>
                <span class="body-small">Back</span>
            </button>

            <div class="flex items-center gap-2 px-3 pt-5 pb-3">
                <i class="bi bi-microsoft text-[#0078D4]" aria-hidden="true"></i>
                <span class="body-bold text-valence-grey-900">WorkIQ</span>
                <span class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-valence-grey-100 text-valence-grey-600">Preview</span>
            </div>

            <nav class="flex flex-col gap-0.5">
                <button
                    v-for="tab in TABS"
                    :key="tab.id"
                    type="button"
                    :data-testid="`workiq-playground-tab-${tab.id}`"
                    :aria-current="activeTab === tab.id ? 'page' : undefined"
                    class="flex items-center gap-2.5 rounded-lg px-3 py-2 body-small text-left transition-colors"
                    :class="activeTab === tab.id ? 'bg-[#F0F4FF] text-[#0078D4] font-semibold' : 'text-valence-grey-750 hover:bg-valence-grey-50'"
                    @click="activeTab = tab.id">
                    <i :class="tab.icon" aria-hidden="true"></i>
                    <span>{{ tab.label }}</span>
                </button>

                <hr class="my-2 border-gray-200" />

                <button
                    type="button"
                    data-testid="workiq-playground-snapshots"
                    class="flex items-center gap-2.5 rounded-lg px-3 py-2 body-small text-left text-valence-grey-750 hover:bg-valence-grey-50 transition-colors"
                    @click="router.visit(workiq_snapshots_url)">
                    <i class="bi bi-archive" aria-hidden="true"></i>
                    <span>Saved snapshots</span>
                </button>

                <!-- Staging-only data-creation tools; lives on its own page. Hidden in prod
                     (the backend route 404s there), signalled by an empty url. -->
                <button
                    v-if="workiq_data_tools_url"
                    type="button"
                    data-testid="workiq-playground-data-tools"
                    class="flex items-center gap-2.5 rounded-lg px-3 py-2 body-small text-left text-valence-grey-750 hover:bg-valence-grey-50 transition-colors"
                    @click="router.visit(workiq_data_tools_url)">
                    <i class="bi bi-database-add" aria-hidden="true"></i>
                    <span>Data tools</span>
                </button>
            </nav>
        </aside>

        <main class="flex-1 overflow-y-auto">
            <div class="mx-auto max-w-3xl flex flex-col gap-6 px-5 md:px-16 py-10">
                <BasePageTitle title="WorkIQ Playground" />
                <p class="body-small text-valence-grey-750 -mt-4">
                    Send a natural-language prompt to the Microsoft Work IQ agent and see its response, grounded in your Microsoft 365 data.
                </p>

                <!-- PROMPT TAB: connection + ad-hoc ask -->
                <div v-show="activeTab === 'prompt'">
                    <div class="flex flex-col gap-6">
                        <div class="flex flex-col gap-2">
                            <div class="flex items-center justify-between p-5 bg-white rounded-xl border-2 border-[#E8E8E8] gap-4">
                                <div class="flex items-center gap-4 min-w-0">
                                    <div class="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-[#F0F4FF]">
                                        <i class="bi bi-microsoft text-[#0078D4] text-lg" aria-hidden="true"></i>
                                    </div>
                                    <div class="min-w-0">
                                        <div class="flex items-center gap-2">
                                            <div class="body-bold text-valence-grey-900">Work IQ</div>
                                            <span class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-valence-grey-100 text-valence-grey-600"
                                                >Preview</span
                                            >
                                        </div>
                                        <div class="body-small text-valence-grey-750">
                                            {{ connected ? expiryLabel : "Connect to query your Microsoft 365 data" }}
                                        </div>
                                    </div>
                                </div>
                                <div class="flex items-center gap-3 flex-shrink-0">
                                    <template v-if="connected">
                                        <span class="hidden sm:flex items-center gap-1.5 body-small text-green-600">
                                            <span class="w-2 h-2 rounded-full bg-green-500"></span>Connected
                                        </span>
                                        <BaseButton theme="tertiary" size="sm" :disabled="refreshing || disconnecting" @click="refresh">
                                            {{ refreshing ? "Refreshing…" : "Refresh" }}
                                        </BaseButton>
                                        <BaseButton theme="tertiary" size="sm" :disabled="refreshing || disconnecting" @click="disconnect">
                                            {{ disconnecting ? "Disconnecting…" : "Disconnect" }}
                                        </BaseButton>
                                    </template>
                                    <BaseButton v-else theme="neutral" size="sm" @click="connect">Connect</BaseButton>
                                </div>
                            </div>
                            <div v-if="refreshError" class="rounded-md border border-red-300 bg-red-50 px-4 py-2 body-small text-red-800 whitespace-pre-wrap break-words">
                                {{ refreshError }}
                            </div>
                        </div>

                        <div class="flex flex-col gap-2">
                            <!-- Target a specific agent (PLT-1615) — defaults to the tenant's default (BizChat) agent. -->
                            <label v-if="connected" for="workiq-ask-agent" class="flex flex-col gap-1">
                                <span class="body-bold text-valence-grey-900">Agent</span>
                                <select
                                    id="workiq-ask-agent"
                                    v-model="selectedAskAgentId"
                                    data-testid="workiq-ask-agent"
                                    class="w-full px-3 py-2 border-2 border-[#E8E8E8] rounded-xl bg-white focus:outline-none focus:border-valence-blue">
                                    <option value="">Default agent</option>
                                    <option v-for="agent in agents" :key="agent.agentId" :value="agent.agentId">
                                        {{ agent.name || agent.agentId }}
                                    </option>
                                </select>
                            </label>
                            <label for="workiq-prompt" class="flex flex-col gap-2">
                                <span class="body-bold text-valence-grey-900">Prompt</span>
                                <textarea
                                    id="workiq-prompt"
                                    v-model="prompt"
                                    rows="4"
                                    placeholder="e.g. Summarize my recent emails about Project Contoso."
                                    class="w-full px-3 py-2 border-2 border-[#E8E8E8] rounded-xl focus:outline-none focus:border-valence-blue"
                                    @keydown.meta.enter="ask" />
                            </label>
                            <div class="flex items-center gap-3">
                                <BaseButton theme="primary" size="sm" :disabled="loading || !prompt.trim()" @click="ask">
                                    {{ loading ? "Streaming…" : "Send" }}
                                </BaseButton>
                                <span class="text-xs text-valence-grey-500">⌘ + Enter to send</span>
                                <span v-if="loading || elapsedMs > 0" class="ml-auto flex items-center gap-3 text-xs tabular-nums text-valence-grey-500">
                                    <span v-if="ttftLabel" :title="serverTtftLabel ? `Work IQ gateway TTFT: ${serverTtftLabel}` : undefined">
                                        first token {{ ttftLabel }}<template v-if="serverTtftLabel"> (gateway {{ serverTtftLabel }})</template>
                                    </span>
                                    <span>total {{ elapsedLabel }}</span>
                                </span>
                            </div>
                        </div>

                        <div v-if="error" class="rounded-md border border-red-300 bg-red-50 px-4 py-3 body-small text-red-800 whitespace-pre-wrap break-words">
                            {{ error }}
                        </div>

                        <div v-if="answer" class="flex flex-col gap-2">
                            <div class="body-bold text-valence-grey-900">Response</div>
                            <MarkdownText :text="answer" class="prose prose-sm max-w-none rounded-xl border-2 border-[#E8E8E8] bg-white px-4 py-3 text-valence-grey-900" />
                        </div>
                    </div>
                </div>

                <!-- FETCH TAB: structured MCP lookups by resource path — the sub-second
                     counterpart to the Prompt tab's `ask` (PLT-1919). -->
                <div v-show="activeTab === 'fetch'">
                    <WorkIqFetchPanel :connected="connected" :mcp-info-url="workiq_mcp_info_url" :fetch-url="workiq_fetch_url" />
                </div>

                <!-- AGENTS TAB: enumerate the tenant's A2A agents (GET /a2a/.agents) and inspect each
                     agent's Agent Card — notably capabilities.streaming (PLT-1615). -->
                <div v-show="activeTab === 'agents'">
                    <div class="flex flex-col gap-6">
                        <p class="body-small text-valence-grey-750 -mt-2">
                            Agents available to your tenant via Work IQ A2A discovery. Select one to inspect its Agent Card — name, version, skills, and advertised capabilities
                            such as streaming.
                        </p>

                        <div v-if="!connected" class="rounded-xl border-2 border-[#E8E8E8] bg-white p-5 body-small text-valence-grey-750">
                            Connect Work IQ on the Prompt tab to list available agents.
                        </div>

                        <template v-else>
                            <!-- Agent list (GET /a2a/.agents) -->
                            <div class="flex flex-col gap-2 p-5 bg-white rounded-xl border-2 border-[#E8E8E8]">
                                <div class="flex items-center justify-between gap-3">
                                    <div class="body-bold text-valence-grey-900">Available agents</div>
                                    <BaseButton theme="tertiary" size="sm" :disabled="agentsLoading" @click="fetchAgents">
                                        {{ agentsLoading ? "Checking…" : "Check agents" }}
                                    </BaseButton>
                                </div>

                                <div v-if="agentsError" class="rounded-md border border-red-300 bg-red-50 px-4 py-2 body-small text-red-800 whitespace-pre-wrap break-words">
                                    {{ agentsError }}
                                </div>

                                <ul v-else-if="agents.length" class="flex flex-col divide-y divide-gray-100">
                                    <li v-for="agent in agents" :key="agent.agentId" class="py-1">
                                        <button
                                            type="button"
                                            class="flex w-full items-center justify-between gap-3 py-2 text-left"
                                            :data-testid="`workiq-agent-row-${agent.agentId}`"
                                            :aria-expanded="Boolean(expanded[agent.agentId])"
                                            @click="toggleAgent(agent.agentId)">
                                            <div class="min-w-0">
                                                <div class="body-small font-semibold text-valence-grey-900 truncate">{{ agent.name || "(unnamed agent)" }}</div>
                                                <div class="text-xs text-valence-grey-500 truncate">
                                                    <span v-if="agent.provider">{{ agent.provider }} · </span><span class="font-mono">{{ agent.agentId }}</span>
                                                </div>
                                            </div>
                                            <i
                                                class="bi flex-shrink-0 text-valence-grey-500"
                                                :class="expanded[agent.agentId] ? 'bi-chevron-up' : 'bi-chevron-down'"
                                                aria-hidden="true"></i>
                                        </button>

                                        <!-- Lazily fetches this agent's card on expand (component mounts only when open). -->
                                        <WorkIqAgentCard v-if="expanded[agent.agentId]" :agent-id="agent.agentId" :card-url="workiq_agent_card_url" />
                                    </li>
                                </ul>

                                <div v-else-if="agentsLoading" class="body-small text-valence-grey-500">Loading agents…</div>
                                <div v-else class="body-small text-valence-grey-500">No agents found.</div>
                            </div>
                        </template>
                    </div>
                </div>

                <!-- PIPELINE TAB: child sub-nav — one entry per POC pillar (PLT-1506, PLT-1613).
                     Each pillar's ask persists a scoped WorkIQ snapshot; there is no automatic pipeline. -->
                <div v-show="activeTab === 'pipeline'">
                    <div class="flex flex-col gap-4">
                        <nav class="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
                            <button
                                v-for="pillar in workiq_pillars"
                                :key="pillar.key"
                                type="button"
                                :data-testid="`workiq-pipeline-child-${pillar.key}`"
                                :disabled="!pillar.available"
                                :title="pillar.available ? undefined : 'Coming soon'"
                                class="rounded-lg px-3 py-1.5 body-small transition-colors"
                                :class="[
                                    pipelineChild === pillar.key ? 'bg-[#F0F4FF] text-[#0078D4] font-semibold' : 'text-valence-grey-750 hover:bg-valence-grey-50',
                                    pillar.available ? '' : 'opacity-40 cursor-not-allowed hover:bg-transparent',
                                ]"
                                @click="pillar.available && (pipelineChild = pillar.key)">
                                {{ pillar.label }}
                                <span v-if="!pillar.available" class="text-[10px] uppercase tracking-wide ml-1">soon</span>
                            </button>
                        </nav>

                        <div v-if="!workiq_pillars.some((p) => p.available)" class="body-small text-valence-grey-500">No pillars are available yet.</div>

                        <!-- PILLAR CHILDREN: manual per-pillar ask + distill → scoped snapshot (PLT-1506, PLT-1613) -->
                        <template v-for="pillar in workiq_pillars" :key="pillar.key">
                            <WorkIqPillarPanel
                                v-if="pillar.available && pipelineChild === pillar.key"
                                :spec="pillar"
                                :ask-url="workiq_ask_pillar_url"
                                :distill-url="workiq_distill_pillar_url"
                                :status-url="workiq_run_insights_status_url"
                                :distill-template="workiq_distill_template"
                                :connected="connected" />
                        </template>
                    </div>
                </div>

                <!-- CALL LOGS TAB: query the Work IQ responses we logged to the datalake -->
                <div v-show="activeTab === 'logs'">
                    <div class="flex flex-col gap-3">
                        <div class="flex items-center justify-between gap-4">
                            <div class="min-w-0">
                                <div class="body-bold text-valence-grey-900">Work IQ call logs</div>
                                <div class="body-small text-valence-grey-750">Your most recent Work IQ A2A responses, captured to the datalake.</div>
                            </div>
                            <BaseButton theme="tertiary" size="sm" :disabled="logsLoading" @click="loadLogs">
                                {{ logsLoading ? "Loading…" : "Refresh" }}
                            </BaseButton>
                        </div>

                        <div v-if="logsError" class="rounded-md border border-red-300 bg-red-50 px-4 py-3 body-small text-red-800 whitespace-pre-wrap break-words">
                            {{ logsError }}
                        </div>
                        <div v-else-if="!logsConfigured" class="body-small text-valence-grey-500">
                            The datalake bucket isn't configured in this environment, so no call logs are stored here.
                        </div>
                        <div v-else-if="logsLoaded && !logs.length" class="body-small text-valence-grey-500">No Work IQ calls logged yet.</div>

                        <div v-for="(log, i) in logs" :key="i" class="rounded-xl border-2 border-[#E8E8E8] bg-white px-4 py-3 flex flex-col gap-2">
                            <div class="flex items-center gap-2 flex-wrap">
                                <span class="text-xs text-valence-grey-500 tabular-nums">{{ formatDate(log.created_at) }}</span>
                                <span class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-valence-grey-100 text-valence-grey-600">{{
                                    log.source
                                }}</span>
                                <span v-if="log.call_code" class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#F0F4FF] text-[#0078D4]">{{
                                    log.call_code
                                }}</span>
                                <span
                                    class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
                                    :class="log.status === 'ok' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
                                    {{ log.status }}
                                </span>
                                <span v-if="log.duration_ms != null" class="ml-auto text-xs tabular-nums text-valence-grey-500">{{ (log.duration_ms / 1000).toFixed(1) }}s</span>
                            </div>

                            <div v-if="log.error" class="body-small text-red-800 whitespace-pre-wrap break-words">{{ log.error }}</div>

                            <details v-if="log.prompt" class="text-valence-grey-900">
                                <summary class="body-small cursor-pointer text-valence-grey-700">Prompt</summary>
                                <pre class="mt-1 text-xs whitespace-pre-wrap break-words bg-valence-grey-50 rounded-md p-2">{{ log.prompt }}</pre>
                            </details>
                            <details v-if="log.answer" class="text-valence-grey-900">
                                <summary class="body-small cursor-pointer text-valence-grey-700">Answer</summary>
                                <MarkdownText :text="log.answer" class="prose prose-sm max-w-none mt-1" />
                            </details>
                            <details v-if="log.raw_response" class="text-valence-grey-900">
                                <summary class="body-small cursor-pointer text-valence-grey-700">Raw response</summary>
                                <pre class="mt-1 text-xs whitespace-pre-wrap break-words bg-valence-grey-50 rounded-md p-2">{{ pretty(log.raw_response) }}</pre>
                            </details>

                            <details class="text-valence-grey-900">
                                <summary class="body-small cursor-pointer text-valence-grey-700 select-none">
                                    Identifiers
                                    <button
                                        type="button"
                                        class="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-valence-grey-100 text-valence-grey-600 hover:bg-valence-grey-200 transition-colors"
                                        @click.stop="copyId(pretty(Object.fromEntries(logIds(log).filter(([, v]) => v !== null))))">
                                        {{ copied === pretty(Object.fromEntries(logIds(log).filter(([, v]) => v !== null))) ? "✓ copied" : "copy all" }}
                                    </button>
                                </summary>
                                <table class="mt-1 w-full text-[11px] border-collapse">
                                    <tbody>
                                        <tr v-for="[key, val] in logIds(log)" :key="key" class="border-t border-[#E8E8E8]">
                                            <td class="py-0.5 pr-3 font-mono text-valence-grey-500 whitespace-nowrap align-middle">{{ key }}</td>
                                            <td v-if="val" class="py-0.5 font-mono text-valence-grey-800 break-all align-middle">{{ val }}</td>
                                            <td v-else class="py-0.5 italic text-valence-grey-300 align-middle">—</td>
                                            <td class="py-0.5 pl-2 align-middle">
                                                <button
                                                    v-if="val"
                                                    type="button"
                                                    class="text-[10px] px-1.5 py-0.5 rounded bg-valence-grey-100 text-valence-grey-600 hover:bg-valence-grey-200 transition-colors"
                                                    @click="copyId(val)">
                                                    {{ copied === val ? "✓" : "copy" }}
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </details>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>

<script lang="ts">
// Staff-only playground reached from the admin sidebar. Rendered standalone (no main-app
// layout) with its own inline sidebar — back button, brand, and tab nav — so the whole
// preview tool reads as an /admin/ page and can be deleted as a single unit later.
export interface WorkIqCallLog {
    created_at: string;
    source: string;
    call_code: string | null;
    status: string;
    duration_ms: number | null;
    prompt: string | null;
    answer: string | null;
    error: string | null;
    raw_response: unknown;
    // App-side context ids (unchanged).
    request_id: string | null;
    correlation_id: string | null;
    // MS/A2A ids — new names (PLT-1568). Old records carry workiq_request_id; fall back in template.
    workiq_jsonrpc_id: string | null;
    workiq_request_id: string | null; // legacy field on old S3 records
    client_request_id: string | null;
    ms_cv: string | null;
    a2a_context_id: string | null;
    a2a_task_id: string | null;
}
</script>

<script setup lang="ts">
import { getCookie } from "/js/utils";
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import WorkIqAgentCard from "~vue/components/workiq/WorkIqAgentCard.vue";
import WorkIqFetchPanel from "~vue/components/workiq/WorkIqFetchPanel.vue";
import WorkIqPillarPanel, { type PillarSpec } from "~vue/components/workiq/WorkIqPillarPanel.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { computed, onUnmounted, reactive, ref, watch } from "vue";

const props = withDefaults(
    defineProps<{
        workiq_connected?: boolean;
        workiq_connect_url?: string;
        workiq_ask_url?: string;
        workiq_ask_stream_url?: string;
        workiq_agent_card_url?: string;
        workiq_list_agents_url?: string;
        workiq_mcp_info_url?: string;
        workiq_fetch_url?: string;
        workiq_ask_pillar_url?: string;
        workiq_distill_pillar_url?: string;
        workiq_pillars?: PillarSpec[];
        workiq_distill_template?: string;
        workiq_run_insights_status_url?: string;
        workiq_call_logs_url?: string;
        workiq_refresh_url?: string;
        workiq_disconnect_url?: string;
        workiq_access_token_expires_at?: string | null;
        workiq_data_tools_url?: string;
        workiq_snapshots_url?: string;
    }>(),
    {
        workiq_connected: false,
        workiq_connect_url: "/workiq/connect/",
        workiq_ask_url: "/workiq/api/ask/",
        workiq_ask_stream_url: "/workiq/api/ask-stream/",
        workiq_agent_card_url: "/workiq/api/agent-card/",
        workiq_list_agents_url: "/workiq/api/agents/",
        workiq_mcp_info_url: "/workiq/api/mcp-info/",
        workiq_fetch_url: "/workiq/api/fetch/",
        workiq_ask_pillar_url: "/workiq/api/ask-pillar/",
        workiq_distill_pillar_url: "/workiq/api/distill-pillar/",
        workiq_pillars: () => [],
        workiq_distill_template: "",
        workiq_run_insights_status_url: "/workiq/api/run-insights/status/",
        workiq_call_logs_url: "/workiq/api/call-logs/",
        workiq_refresh_url: "/workiq/api/refresh/",
        workiq_disconnect_url: "/workiq/api/disconnect/",
        workiq_access_token_expires_at: null,
        workiq_data_tools_url: "/admin/workiq/playground/data/",
        workiq_snapshots_url: "/admin/workiq/playground/snapshots/",
    },
);

function handleGoBack() {
    // Prefer the actual previous page; fall back to the admin home when opened directly.
    if (globalThis.history && globalThis.history.length > 1) {
        globalThis.history.back();
    } else {
        router.visit("/admin/");
    }
}

const TABS = [
    { id: "prompt", label: "Prompt", icon: "bi bi-chat-left-text" },
    { id: "fetch", label: "Fetch", icon: "bi bi-lightning-charge" },
    { id: "agents", label: "Agents", icon: "bi bi-robot" },
    { id: "pipeline", label: "Pipeline", icon: "bi bi-diagram-3" },
    { id: "logs", label: "Call logs", icon: "bi bi-list-ul" },
] as const;
const VALID_TABS = TABS.map((tab) => tab.id) as readonly string[];
const hashTab = globalThis.location.hash.replace("#", "");
const activeTab = ref(VALID_TABS.includes(hashTab) ? hashTab : "prompt");

const prompt = ref("");
const answer = ref("");
const error = ref("");
const loading = ref(false);

// Connection / token-expiry state.
const connected = ref(props.workiq_connected);
const expiresAt = ref<string | null>(props.workiq_access_token_expires_at);
const refreshing = ref(false);
const disconnecting = ref(false);
const refreshError = ref("");
const now = ref(Date.now());
const nowHandle = setInterval(() => (now.value = Date.now()), 1000);

const expiryLabel = computed(() => {
    if (!expiresAt.value) {
        return "Connected";
    }
    const ms = new Date(expiresAt.value).getTime() - now.value;
    if (ms <= 0) {
        return "Access token expired — click Refresh";
    }
    const totalSec = Math.floor(ms / 1000);
    return `Access token expires in ${Math.floor(totalSec / 60)}m ${totalSec % 60}s`;
});

async function refresh() {
    if (refreshing.value) {
        return;
    }
    refreshing.value = true;
    refreshError.value = "";
    try {
        const { data, response } = await useFetch(props.workiq_refresh_url).post({}).json();
        if (response.value?.ok) {
            expiresAt.value = data.value?.access_token_expiration ?? expiresAt.value;
        } else {
            refreshError.value = data.value?.error || "Refresh failed.";
        }
    } finally {
        refreshing.value = false;
    }
}

async function disconnect() {
    if (disconnecting.value) {
        return;
    }
    disconnecting.value = true;
    refreshError.value = "";
    try {
        const { response } = await useFetch(props.workiq_disconnect_url).post({}).json();
        if (response.value?.ok) {
            connected.value = false;
            expiresAt.value = null;
        } else {
            refreshError.value = "Disconnect failed.";
        }
    } finally {
        disconnecting.value = false;
    }
}

// Agent discovery (PLT-1615): list the tenant's A2A agents (GET /a2a/.agents). Each row
// expands to reveal that agent's card, fetched lazily by <WorkIqAgentCard> on expand.
interface AgentInfo {
    agentId: string;
    name?: string;
    provider?: string;
}

const agents = ref<AgentInfo[]>([]);
const agentsError = ref("");
const agentsLoading = ref(false);
// agentId -> whether its card row is expanded.
const expanded = reactive<Record<string, boolean>>({});
// Prompt-tab target agent for ask/stream ("" = the tenant's default/BizChat agent).
const selectedAskAgentId = ref("");

async function fetchAgents() {
    if (agentsLoading.value) {
        return;
    }
    agentsLoading.value = true;
    agentsError.value = "";
    try {
        const { data, response } = await useFetch(props.workiq_list_agents_url).get().json();
        if (response.value?.ok) {
            agents.value = data.value?.agents ?? [];
        } else {
            agents.value = [];
            agentsError.value = data.value?.error || "Failed to list agents.";
        }
    } finally {
        agentsLoading.value = false;
    }
}

function toggleAgent(agentId: string) {
    expanded[agentId] = !expanded[agentId];
}

// Populate the agent list as soon as the page loads (if connected), so the Agents tab is
// ready without a manual click. Cards are fetched on demand when a row is expanded.
if (connected.value) {
    fetchAgents();
}

// Elapsed-time timer for the ad-hoc ask, plus time-to-first-visible-token (PLT-1662).
// ttftMs is the client-perceived latency from clicking Send to the first streamed token
// rendered in the bubble; serverTtftMs is the gateway-side TTFT reported by the done frame,
// so we can separate network + our overhead from Work IQ's own think time.
const elapsedMs = ref(0);
const ttftMs = ref<number | null>(null);
const serverTtftMs = ref<number | null>(null);
let timerStart = 0;
let timerHandle: ReturnType<typeof setInterval> | undefined;
const elapsedLabel = computed(() => `${(elapsedMs.value / 1000).toFixed(1)}s`);
const ttftLabel = computed(() => (ttftMs.value === null ? null : `${(ttftMs.value / 1000).toFixed(1)}s`));
const serverTtftLabel = computed(() => (serverTtftMs.value === null ? null : `${(serverTtftMs.value / 1000).toFixed(1)}s`));

function startTimer() {
    timerStart = performance.now();
    elapsedMs.value = 0;
    ttftMs.value = null;
    serverTtftMs.value = null;
    timerHandle = setInterval(() => {
        elapsedMs.value = performance.now() - timerStart;
    }, 100);
}

function stopTimer() {
    if (timerHandle) {
        clearInterval(timerHandle);
        timerHandle = undefined;
    }
    elapsedMs.value = performance.now() - timerStart;
}

function connect() {
    globalThis.location.href = props.workiq_connect_url;
}

// Stream the answer from the A2A SendStreamingMessage endpoint, rendering tokens as they
// arrive. Native fetch (not useFetch) is required to read the response body as a stream;
// this mirrors the SSE-over-POST readers elsewhere (StreamingPostClient, Nadia2Chat/sse.ts).
async function ask() {
    if (loading.value || !prompt.value.trim()) {
        return;
    }
    loading.value = true;
    answer.value = "";
    error.value = "";
    startTimer();

    try {
        const response = await fetch(props.workiq_ask_stream_url, {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCookie("csrftoken") || "",
            },
            body: JSON.stringify({ prompt: prompt.value, agent_id: selectedAskAgentId.value }),
        });

        if (!response.ok || !response.body) {
            const text = await response.text().catch(() => response.statusText);
            error.value = `Request failed: HTTP ${response.status} ${text}`;
            return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        const handleFrame = (frame: string) => {
            const trimmed = frame.trim();
            if (!trimmed || trimmed.startsWith(":")) {
                return;
            }
            const dataLine = trimmed.split("\n").find((line) => line.startsWith("data:"));
            if (!dataLine) {
                return;
            }
            let payload: Record<string, unknown>;
            try {
                payload = JSON.parse(dataLine.slice("data:".length).trim());
            } catch {
                return;
            }
            if (payload.type === "delta" && typeof payload.text === "string") {
                if (ttftMs.value === null) {
                    ttftMs.value = performance.now() - timerStart;
                }
                answer.value += payload.text;
            } else if (payload.type === "done") {
                if (typeof payload.ttft_ms === "number") {
                    serverTtftMs.value = payload.ttft_ms;
                }
            } else if (payload.type === "error" && typeof payload.error === "string") {
                error.value = payload.error;
            }
        };

        for (;;) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            const frames = buffer.split("\n\n");
            buffer = frames.pop() ?? "";
            frames.forEach(handleFrame);
        }
        if (buffer.trim()) {
            handleFrame(buffer);
        }

        if (!answer.value && !error.value) {
            answer.value = "(empty response)";
        }
    } catch (err) {
        error.value = err instanceof Error ? err.message : String(err);
    } finally {
        stopTimer();
        loading.value = false;
    }
}

// Pipeline tab: which per-pillar child page is active (each pillar owns its own ask/distill state;
// there is no full-run flow — a pillar's ask persists its scoped snapshot). Default to the first
// available pillar.
const pipelineChild = ref(props.workiq_pillars.find((pillar) => pillar.available)?.key ?? "");

// Call logs: read the Work IQ responses we logged to the datalake. Loaded lazily the
// first time the Call logs tab is opened, plus a manual Refresh.
const logs = ref<WorkIqCallLog[]>([]);
const logsLoading = ref(false);
const logsError = ref("");
const logsConfigured = ref(true);
const logsLoaded = ref(false);

function formatDate(iso: string): string {
    return new Date(iso).toLocaleString();
}

function pretty(value: unknown): string {
    return JSON.stringify(value, null, 2);
}

// Copy-to-clipboard for call log identifiers. `copied` holds the last-copied value so
// the button can briefly show "✓" as confirmation.
const copied = ref<string | null>(null);
let copiedTimer: ReturnType<typeof setTimeout> | null = null;

function copyId(val: string) {
    navigator.clipboard.writeText(val).then(() => {
        copied.value = val;
        if (copiedTimer) {
            clearTimeout(copiedTimer);
        }
        copiedTimer = setTimeout(() => (copied.value = null), 1500);
    });
}

function logIds(log: WorkIqCallLog): [string, string | null][] {
    const ids: [string, string | null][] = [
        ["request_id", log.request_id],
        ["correlation_id", log.correlation_id],
        ["workiq_jsonrpc_id", log.workiq_jsonrpc_id ?? log.workiq_request_id],
        ["client_request_id", log.client_request_id],
        ["ms_cv", log.ms_cv],
        ["a2a_context_id", log.a2a_context_id],
        ["a2a_task_id", log.a2a_task_id],
    ];

    // Pull additional ids from the A2A response body that aren't worth storing as
    // top-level log fields but are useful to copy when contacting MS support.
    const raw = log.raw_response as Record<string, unknown> | null;
    const task = raw?.result && typeof raw.result === "object" ? (raw.result as Record<string, unknown>).task : null;
    if (task && typeof task === "object") {
        const t = task as Record<string, unknown>;
        const msg = t.status && typeof t.status === "object" ? (t.status as Record<string, unknown>).message : null;
        if (msg && typeof msg === "object") {
            const msgId = (msg as Record<string, unknown>).messageId;
            if (typeof msgId === "string") {
                ids.push(["message_id", msgId]);
            }
        }
        const artifacts = Array.isArray(t.artifacts) ? t.artifacts : [];
        artifacts.forEach((artifact: unknown, i: number) => {
            if (artifact && typeof artifact === "object") {
                const aid = (artifact as Record<string, unknown>).artifactId;
                if (typeof aid === "string") {
                    ids.push([artifacts.length === 1 ? "artifact_id" : `artifact_id[${i}]`, aid]);
                }
            }
        });
    }

    return ids;
}

async function loadLogs() {
    if (logsLoading.value) {
        return;
    }
    logsLoading.value = true;
    logsError.value = "";
    try {
        const { data, response } = await useFetch(props.workiq_call_logs_url).get().json();
        if (response.value?.ok) {
            logs.value = data.value?.responses || [];
            logsConfigured.value = data.value?.configured !== false;
        } else {
            logsError.value = data.value?.error || "Failed to load call logs.";
        }
    } finally {
        logsLoading.value = false;
        logsLoaded.value = true;
    }
}

watch(activeTab, (tab) => {
    globalThis.history.replaceState(null, "", `#${tab}`);
    if (tab === "logs" && !logsLoaded.value) {
        loadLogs();
    }
});

onUnmounted(() => {
    if (timerHandle) {
        clearInterval(timerHandle);
    }
    clearInterval(nowHandle);
});
</script>
